import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Lock, Euro, Star, Target, Zap, Calendar, Utensils, Lightbulb, 
  CheckCircle2, FileText, Building
} from 'lucide-react';
import { getBackendUrl } from '../utils/config';

const API = getBackendUrl();

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount || 0);
};

const PublicOverviewPage = () => {
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [requiresPassword, setRequiresPassword] = useState(true);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [data, setData] = useState(null);
  const [shareName, setShareName] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    checkShare();
  }, [shareId]);

  const checkShare = async () => {
    try {
      const res = await axios.get(`${API}/api/public/overview/${shareId}`);
      setShareName(res.data.name);
      setRequiresPassword(res.data.requires_password);
      setLoading(false);
    } catch (err) {
      setError('Dit overzicht bestaat niet of is niet meer beschikbaar.');
      setLoading(false);
    }
  };

  const handleVerifyPassword = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error('Vul een wachtwoord in');
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/api/public/overview/${shareId}/verify`, { password });
      setData(res.data);
      setRequiresPassword(false);
      toast.success('Toegang verleend');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Onjuist wachtwoord');
    } finally {
      setVerifying(false);
    }
  };

  // Calculate statistics from data
  const calcStats = () => {
    if (!data) return {};
    
    const currentYear = new Date().getFullYear();
    const previousYear = currentYear - 1;

    const calcYearTotals = (dataArr, field, year) => {
      return (dataArr || []).filter(d => {
        const date = new Date(d.date || d.period_start);
        return date.getFullYear() === year;
      }).reduce((sum, d) => sum + (d[field] || d.amount || 0), 0);
    };

    const calcYearCosts = (dataArr, year) => {
      return (dataArr || []).filter(c => {
        const date = new Date(c.date || c.period_start);
        return date.getFullYear() === year;
      }).reduce((sum, c) => {
        return sum + (c.food_cost || 0) + (c.beverage_cost || 0) + (c.staff_cost || 0) + 
               (c.fixed_costs || 0) + (c.marketing_cost || 0) + (c.other_cost || 0);
      }, 0);
    };

    const currentRevenue = calcYearTotals(data.revenue, 'amount', currentYear);
    const previousRevenue = calcYearTotals(data.revenue, 'amount', previousYear);
    // If previous year has no data but current year does, show 100% growth
    const revenueGrowth = previousRevenue > 0 
      ? ((currentRevenue - previousRevenue) / previousRevenue) * 100 
      : (currentRevenue > 0 ? 100 : 0);

    const currentCosts = calcYearCosts(data.costs, currentYear);
    const previousCosts = calcYearCosts(data.costs, previousYear);
    // If previous year has no data but current year does, show 100% growth
    const costsChange = previousCosts > 0 
      ? ((currentCosts - previousCosts) / previousCosts) * 100 
      : (currentCosts > 0 ? 100 : 0);

    const googleRating = data.settings?.google_rating || 0;
    const currentYearReviews = (data.reviews || []).filter(r => new Date(r.date || r.created_at).getFullYear() === currentYear).length;
    const previousYearReviews = (data.reviews || []).filter(r => new Date(r.date || r.created_at).getFullYear() === previousYear).length;
    const reviewsGrowth = currentYearReviews - previousYearReviews;

    // Successful Campaigns (completed, afgerond, afgelopen, or with good performance)
    const successfulCampaigns = (data.campaigns || []).filter(c => 
      c.status === 'completed' || c.status === 'afgerond' || c.status === 'voltooid' ||
      c.status === 'afgelopen' ||
      c.performance === 'goed' || c.performance === 'excellent' || c.performance === 'uitstekend'
    );

    // Successful Events (past events or confirmed/completed)
    const successfulEvents = (data.events || []).filter(e => {
      const eventDate = new Date(e.date);
      const isPast = eventDate < new Date();
      return isPast || e.status === 'afgerond' || e.status === 'voltooid' || 
             e.status === 'bevestigd' || e.guests_attended > 0;
    });

    // Successful Arrangements (booked, confirmed, completed)
    const successfulArrangements = (data.arrangements || []).filter(a => 
      a.status === 'completed' || a.status === 'afgerond' || a.status === 'bevestigd' ||
      a.status === 'geboekt' || a.status === 'voltooid'
    );

    const achievedGoals = (data.goals || []).filter(g => g.status === 'completed' || g.status === 'behaald' || g.progress >= 100);

    // Worked out Ideas - include Dutch status values
    const completedIdeas = (data.ideas || []).filter(i => 
      i.status === 'completed' || i.status === 'afgerond' || i.status === 'converted' ||
      i.status === 'geimplementeerd' || i.status === 'voltooid' || i.status === 'uitgevoerd'
    );

    // Calculate profit growth
    const currentProfit = currentRevenue - currentCosts;
    const previousProfit = previousRevenue - previousCosts;
    const profitGrowth = previousProfit > 0 
      ? ((currentProfit - previousProfit) / previousProfit) * 100 
      : (currentProfit > 0 ? 100 : 0);
    
    // Calculate profit margin growth
    const currentMargin = currentRevenue > 0 ? (currentProfit / currentRevenue) * 100 : 0;
    const previousMargin = previousRevenue > 0 ? (previousProfit / previousRevenue) * 100 : 0;
    const marginChange = previousMargin > 0 
      ? currentMargin - previousMargin 
      : (currentMargin > 0 ? currentMargin : 0);

    return {
      currentYear, previousYear,
      currentRevenue, previousRevenue, revenueGrowth,
      currentCosts, previousCosts, costsChange,
      currentProfit, profitGrowth, currentMargin, marginChange,
      googleRating, currentYearReviews, reviewsGrowth,
      successfulCampaigns, successfulEvents, successfulArrangements,
      achievedGoals, completedIdeas
    };
  };

  const stats = calcStats();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-50 to-stone-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1A2E05] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-stone-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-50 to-stone-100 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-rose-600" />
            </div>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">Niet beschikbaar</h2>
            <p className="text-stone-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (requiresPassword && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-50 to-stone-100 p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-[#1A2E05]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building className="w-8 h-8 text-[#1A2E05]" />
            </div>
            <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>
              Beveiligd Bedrijfsoverzicht
            </CardTitle>
            {shareName && (
              <p className="text-sm text-stone-500 mt-1">{shareName}</p>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerifyPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Wachtwoord</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Voer het wachtwoord in"
                  autoFocus
                  data-testid="overview-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#1A2E05] hover:bg-[#2A4208]"
                disabled={verifying}
                data-testid="overview-verify-btn"
              >
                {verifying ? 'Controleren...' : 'Bekijk Overzicht'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show the overview data
  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#1A2E05] mb-2" style={{ fontFamily: 'Manrope' }}>
            Bedrijfsoverzicht {stats.currentYear}
          </h1>
          {shareName && <p className="text-stone-600">{shareName}</p>}
          <p className="text-sm text-stone-500 mt-2">
            Gegenereerd op {new Date(data.generated_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Financial Summary */}
        <Card className="bg-gradient-to-br from-[#1A2E05] to-[#2A4208] text-white overflow-hidden shadow-xl">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Euro className="w-5 h-5" />
              Financiële Samenvatting {stats.currentYear}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
                <p className="text-white/70 text-sm">Omzet YTD</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.currentRevenue)}</p>
                <p className={`text-sm ${stats.revenueGrowth >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {stats.revenueGrowth >= 0 ? '+' : ''}{stats.revenueGrowth?.toFixed(1)}% vs {stats.previousYear}
                </p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
                <p className="text-white/70 text-sm">Kosten YTD</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.currentCosts)}</p>
                <p className={`text-sm ${stats.costsChange <= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {stats.costsChange >= 0 ? '+' : ''}{stats.costsChange?.toFixed(1)}% vs {stats.previousYear}
                </p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
                <p className="text-white/70 text-sm">Winst YTD</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.currentProfit)}</p>
                <p className={`text-sm ${stats.profitGrowth >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {stats.profitGrowth >= 0 ? '+' : ''}{stats.profitGrowth?.toFixed(1)}% vs {stats.previousYear}
                </p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
                <p className="text-white/70 text-sm">Winstmarge</p>
                <p className="text-2xl font-bold">
                  {stats.currentMargin?.toFixed(1)}%
                </p>
                <p className={`text-sm ${stats.marginChange >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {stats.marginChange >= 0 ? '+' : ''}{stats.marginChange?.toFixed(1)}% vs {stats.previousYear}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Google Reviews */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Star className="w-5 h-5 text-amber-500" />
              Google Reviews
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-amber-50 rounded-xl">
                <div className="flex items-center justify-center gap-1 mb-2">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className={`w-5 h-5 ${i <= Math.round(stats.googleRating) ? 'text-amber-500 fill-amber-500' : 'text-stone-300'}`} />
                  ))}
                </div>
                <p className="text-3xl font-bold text-amber-600">{stats.googleRating?.toFixed(1)}</p>
                <p className="text-sm text-stone-600">Google Cijfer</p>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-xl">
                <p className="text-3xl font-bold text-[#1A2E05]">{stats.currentYearReviews}</p>
                <p className="text-sm text-stone-600">Reviews {stats.currentYear}</p>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-xl">
                <p className={`text-3xl font-bold ${stats.reviewsGrowth >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {stats.reviewsGrowth >= 0 ? '+' : ''}{stats.reviewsGrowth}
                </p>
                <p className="text-sm text-stone-600">vs {stats.previousYear}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Achieved Goals */}
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
                <span className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-600" />
                  Behaalde Doelen
                </span>
                <Badge className="bg-emerald-100 text-emerald-700">{stats.achievedGoals?.length || 0}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!stats.achievedGoals || stats.achievedGoals.length === 0) ? (
                <p className="text-stone-500 text-sm py-4 text-center">Nog geen doelen behaald</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {stats.achievedGoals.slice(0, 5).map((goal, idx) => (
                    <div key={goal.id || idx} className="flex items-center gap-3 p-2 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <span className="text-sm font-medium truncate">{goal.title || goal.name}</span>
                    </div>
                  ))}
                  {stats.achievedGoals.length > 5 && (
                    <p className="text-xs text-stone-500 text-center">+ {stats.achievedGoals.length - 5} meer</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Successful Campaigns */}
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
                <span className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-600" />
                  Succesvolle Campagnes
                </span>
                <Badge className="bg-purple-100 text-purple-700">{stats.successfulCampaigns?.length || 0}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!stats.successfulCampaigns || stats.successfulCampaigns.length === 0) ? (
                <p className="text-stone-500 text-sm py-4 text-center">Nog geen afgeronde campagnes</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {stats.successfulCampaigns.slice(0, 5).map((campaign, idx) => (
                    <div key={campaign.id || idx} className="flex items-center gap-3 p-2 bg-purple-50 rounded-lg">
                      <Zap className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <span className="text-sm font-medium truncate">{campaign.name || campaign.title}</span>
                    </div>
                  ))}
                  {stats.successfulCampaigns.length > 5 && (
                    <p className="text-xs text-stone-500 text-center">+ {stats.successfulCampaigns.length - 5} meer</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Successful Events - Top 4 */}
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
                <span className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Top Events
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-500">
                    {(stats.successfulEvents || []).reduce((sum, e) => sum + (e.guests_attended || e.expected_guests || 0), 0)} bezoekers
                  </span>
                  <Badge className="bg-blue-100 text-blue-700">{stats.successfulEvents?.length || 0}</Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!stats.successfulEvents || stats.successfulEvents.length === 0) ? (
                <p className="text-stone-500 text-sm py-4 text-center">Nog geen events geweest</p>
              ) : (
                <div className="space-y-2">
                  {[...stats.successfulEvents]
                    .sort((a, b) => (b.guests_attended || b.expected_guests || 0) - (a.guests_attended || a.expected_guests || 0))
                    .slice(0, 4)
                    .map((event, index) => (
                    <div key={event.id || index} className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{event.name || event.title}</p>
                        <p className="text-xs text-stone-500">
                          {event.date && new Date(event.date).toLocaleDateString('nl-NL')}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-blue-600">{event.guests_attended || event.expected_guests || 0}</p>
                        <p className="text-xs text-stone-500">bezoekers</p>
                      </div>
                    </div>
                  ))}
                  {stats.successfulEvents.length > 4 && (
                    <p className="text-xs text-stone-500 text-center pt-2">+ {stats.successfulEvents.length - 4} meer</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Arrangements - Grouped by type */}
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
                <span className="flex items-center gap-2">
                  <Utensils className="w-5 h-5 text-orange-600" />
                  Top Arrangementen
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-500">
                    {stats.successfulArrangements?.length || 0} boekingen
                  </span>
                  <Badge className="bg-orange-100 text-orange-700">{(stats.successfulArrangements || []).reduce((sum, a) => sum + (a.actual_guests || a.min_guests || 0), 0)} gasten</Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!stats.successfulArrangements || stats.successfulArrangements.length === 0) ? (
                <p className="text-stone-500 text-sm py-4 text-center">Nog geen arrangementen</p>
              ) : (
                <div className="space-y-2">
                  {(() => {
                    // Group arrangements by name and sum guests + count bookings
                    const grouped = (stats.successfulArrangements || []).reduce((acc, arr) => {
                      const name = arr.name || 'Overig';
                      if (!acc[name]) {
                        acc[name] = { 
                          name, 
                          totalGuests: 0, 
                          bookings: 0 
                        };
                      }
                      acc[name].totalGuests += (arr.actual_guests || arr.min_guests || 0);
                      acc[name].bookings += 1;
                      return acc;
                    }, {});
                    
                    // Convert to array and sort by total guests
                    const sortedArrangements = Object.values(grouped)
                      .sort((a, b) => b.totalGuests - a.totalGuests)
                      .slice(0, 4);
                    
                    return sortedArrangements.map((arr, index) => (
                      <div key={arr.name} className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-orange-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{arr.name}</p>
                          <p className="text-xs text-stone-500">
                            {arr.bookings} {arr.bookings === 1 ? 'boeking' : 'boekingen'}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-lg font-bold text-orange-600">{arr.totalGuests}</p>
                          <p className="text-xs text-stone-500">gasten totaal</p>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Completed Ideas */}
        <Card className="shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
              <span className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-600" />
                Uitgewerkte Ideeën
              </span>
              <Badge className="bg-yellow-100 text-yellow-700">{stats.completedIdeas?.length || 0}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!stats.completedIdeas || stats.completedIdeas.length === 0) ? (
              <p className="text-stone-500 text-sm py-4 text-center">Nog geen ideeën uitgewerkt</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-2">
                {stats.completedIdeas.slice(0, 6).map((idea, idx) => (
                  <div key={idea.id || idx} className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
                    <Lightbulb className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{idea.title}</p>
                      {idea.description && <p className="text-xs text-stone-500 truncate">{idea.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-stone-500 pt-4 border-t">
          <p>Dit overzicht is gegenereerd vanuit Het Horeca Dashboard</p>
        </div>
      </div>
    </div>
  );
};

export default PublicOverviewPage;
